import { Buffer } from '@pixi/core';
import type { IPoint } from '@pixi/math';
import { MeshGeometry } from '@pixi/mesh';
import { TYPES } from 'pixi.js';

const defaultSettings = {
    thickness: 1,
    textureScale: 1,
    width: 256,
};

export type TrailGeometrySettings = typeof defaultSettings;

export class TrailGeometry extends MeshGeometry
{
    public settings: TrailGeometrySettings;
    public points: IPoint[];
    public alphas: number[];

    constructor(points: IPoint[], alphas: number[], settings: Partial<TrailGeometrySettings> = {})
    {
        if (points.length !== alphas.length) throw new Error('Points and alphas length must match');

        super(new Float32Array(points.length * 4),
            new Float32Array(points.length * 4),
            new Uint16Array((points.length - 1) * 6));

        this.settings = { ...defaultSettings, ...settings };
        this.addAttribute('aAlpha', new Buffer(new Float32Array(points.length * 4)), 1, false, TYPES.FLOAT);
        this.points = points;
        this.alphas = alphas;
        this.build();
    }

    private build(): void
    {
        const points = this.points;

        if (!points) return;

        const vertexBuffer = this.getBuffer('aVertexPosition');
        const uvBuffer = this.getBuffer('aTextureCoord');
        const alphaBuffer = this.getBuffer('aAlpha');
        const indexBuffer = this.getIndex();

        // if too little points, or texture hasn't got UVs set yet just move on.
        if (points.length < 1)
        {
            return;
        }

        // if the number of points has changed we will need to recreate the arraybuffers
        if (vertexBuffer.data.length / 4 !== points.length)
        {
            vertexBuffer.data = new Float32Array(points.length * 4);
            uvBuffer.data = new Float32Array(points.length * 4);
            indexBuffer.data = new Uint16Array((points.length - 1) * 6);
            alphaBuffer.data = new Float32Array(points.length * 4);
        }

        const uvs = uvBuffer.data;
        const indices = indexBuffer.data;

        uvs[0] = 0;
        uvs[1] = 0;
        uvs[2] = 0;
        uvs[3] = 1;

        let amount = 0;
        let prev = points[0];
        const total = points.length; // - 1;

        for (let i = 0; i < total; i++)
        {
            // time to do some smart drawing!
            const index = i * 4;

            // calculate pixel distance from previous point
            const dx = prev.x - points[i].x;
            const dy = prev.y - points[i].y;
            const distance = Math.sqrt((dx * dx) + (dy * dy));

            prev = points[i];
            amount += distance / this.settings.width;

            uvs[index] = amount;
            uvs[index + 1] = 1.0;

            uvs[index + 2] = amount;
            uvs[index + 3] = 0.0;
        }

        let indexCount = 0;

        for (let i = 0; i < total - 1; i++)
        {
            const index = i * 2;

            indices[indexCount++] = index;
            indices[indexCount++] = index + 1;
            indices[indexCount++] = index + 2;

            indices[indexCount++] = index + 2;
            indices[indexCount++] = index + 1;
            indices[indexCount++] = index + 3;
        }

        // ensure that the changes are uploaded
        uvBuffer.update();
        indexBuffer.update();
        this.updateVertices();
    }

    /**
     * refreshes vertices of Trail mesh
     */
    public updateVertices(): void
    {
        const points = this.points;

        if (points.length < 1)
        {
            return;
        }

        let lastPoint = points[0];
        let nextPoint;
        let perpX = 0;
        let perpY = 0;

        const vertices = this.buffers[0].data;
        const alpha = this.buffers[3].data;
        const total = points.length;

        for (let i = 0; i < total; i++)
        {
            const point = points[i];
            const index = i * 4;

            if (i < points.length - 1)
            {
                nextPoint = points[i + 1];
            }
            else
            {
                nextPoint = point;
            }

            perpY = -(nextPoint.x - lastPoint.x);
            perpX = nextPoint.y - lastPoint.y;

            let ratio = (1 - (i / (total - 1))) * 10;

            if (ratio > 1)
            {
                ratio = 1;
            }

            let perpLength = Math.sqrt((perpX * perpX) + (perpY * perpY));

            if (perpLength === 0) perpLength = 1;
            const scale = this.settings.textureScale;
            const width = this.settings.thickness;
            const num = scale > 0 ? scale * width / 2 : width / 2;

            perpX /= perpLength;
            perpY /= perpLength;

            perpX *= num;
            perpY *= num;

            // perpX = Math.abs(perpX);
            // perpY = Math.abs(perpY);

            vertices[index] = point.x + perpX;
            vertices[index + 1] = point.y + perpY;
            vertices[index + 2] = point.x - perpX;
            vertices[index + 3] = point.y - perpY;

            lastPoint = point;

            const a = this.alphas[i];

            alpha[index] = a;
            alpha[index + 1] = a;
            alpha[index + 2] = a;
            alpha[index + 3] = a;
        }

        this.buffers[0].update();
        this.buffers[3].update();
    }

    public update(): void
    {
        if (this.settings.textureScale > 0)
        {
            this.build(); // we need to update UVs
        }
        else
        {
            this.updateVertices();
        }
    }
}
